(() => {
  'use strict';

  const GLOBAL_KEY = 'RFNProductVideo';
  const GALLERY_SELECTOR = '[data-rfn-product-gallery]';
  const VIDEO_SELECTOR = 'video[data-rfn-product-video]';
  const ACTIVE_SLIDE_SELECTOR = '.is-selected, .slick-current, .is-active';

  if (window[GLOBAL_KEY]?.initialized) {
    window[GLOBAL_KEY].refresh();
    return;
  }

  const galleries = new Set();
  const observers = new Map();
  const visibility = new WeakMap();
  const userPaused = new WeakSet();
  const programmaticState = new WeakSet();

  let refreshFrame = 0;
  let refreshTimer = 0;

  const getSlide = (video) => video.closest('.product-main-slide');

  const slideIsActive = (slide) => {
    if (!slide) return true;

    if (slide.matches(ACTIVE_SLIDE_SELECTOR)) return true;

    const ariaHidden = slide.getAttribute('aria-hidden');
    if (ariaHidden !== null) return ariaHidden !== 'true';

    const style = window.getComputedStyle(slide);
    if (style.display === 'none' || style.visibility === 'hidden') return false;

    const rect = slide.getBoundingClientRect();
    const viewport = slide.closest('[data-product-photos]') || slide.parentElement;

    if (!viewport) return rect.width > 0 && rect.height > 0;

    const viewportRect = viewport.getBoundingClientRect();
    const overlap = Math.max(
      0,
      Math.min(rect.right, viewportRect.right) -
        Math.max(rect.left, viewportRect.left)
    );

    return rect.width > 0 && overlap / rect.width >= 0.55;
  };

  const galleryIsVisible = (gallery) => visibility.get(gallery) !== false;

  const withProgrammaticState = (video, callback) => {
    programmaticState.add(video);

    try {
      callback();
    } finally {
      window.setTimeout(() => programmaticState.delete(video), 120);
    }
  };

  const setMuted = (video, muted) => {
    withProgrammaticState(video, () => {
      video.defaultMuted = muted;
      video.muted = muted;
      video.volume = 1;
    });
  };

  const muteOtherVideos = (activeVideo) => {
    document.querySelectorAll(VIDEO_SELECTOR).forEach((video) => {
      if (video === activeVideo) return;
      setMuted(video, true);
    });
  };

  const playMuted = async (video) => {
    setMuted(video, true);

    try {
      await video.play();
    } catch (error) {
      // Some browsers may still require a user gesture.
    }
  };

  const attemptAutoplay = async (video) => {
    if (!video || userPaused.has(video)) return;
    if (!video.paused && !video.ended) return;

    muteOtherVideos(video);
    setMuted(video, false);

    try {
      await video.play();
    } catch (error) {
      // Autoplay with sound is commonly blocked.
      // Keep playback seamless by retrying muted.
      await playMuted(video);
    }
  };

  const pauseVideo = (video, resetUserPause = true) => {
    if (resetUserPause) userPaused.delete(video);

    if (!video.paused) {
      withProgrammaticState(video, () => video.pause());
    }
  };

  const syncGallery = (gallery) => {
    const videos = [...gallery.querySelectorAll(VIDEO_SELECTOR)];
    if (!videos.length) return;

    let activeVideo = null;

    videos.forEach((video) => {
      const isActive =
        galleryIsVisible(gallery) &&
        slideIsActive(getSlide(video));

      if (isActive && !activeVideo) {
        activeVideo = video;
      } else {
        pauseVideo(video, true);
      }
    });

    if (activeVideo) attemptAutoplay(activeVideo);
  };

  const refresh = () => {
    refreshFrame = 0;

    document.querySelectorAll(GALLERY_SELECTOR).forEach((gallery) => {
      registerGallery(gallery);
      syncGallery(gallery);
    });
  };

  const scheduleRefresh = (delay = 0) => {
    window.clearTimeout(refreshTimer);

    refreshTimer = window.setTimeout(() => {
      if (refreshFrame) window.cancelAnimationFrame(refreshFrame);
      refreshFrame = window.requestAnimationFrame(refresh);
    }, delay);
  };

  const bindGalleryControls = (gallery) => {
    if (gallery.dataset.rfnVideoBound === 'true') return;
    gallery.dataset.rfnVideoBound = 'true';

    /*
      Do not intercept clicks on the video.
      The browser-native controls own play, pause, sound, timeline
      and fullscreen interactions.
    */
    gallery.addEventListener('click', (event) => {
      if (event.target.closest('[data-product-thumb], .product__thumb-arrow')) {
        scheduleRefresh(80);
        scheduleRefresh(320);
      }
    });

    gallery.querySelectorAll(VIDEO_SELECTOR).forEach((video) => {
      video.playsInline = true;
      video.controls = true;
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.setAttribute('controls', '');

      video.addEventListener('play', () => {
        userPaused.delete(video);

        if (!video.muted && video.volume > 0) {
          muteOtherVideos(video);
        }
      });

      video.addEventListener('pause', () => {
        if (
          !programmaticState.has(video) &&
          slideIsActive(getSlide(video))
        ) {
          userPaused.add(video);
        }
      });

      video.addEventListener('volumechange', () => {
        if (
          !programmaticState.has(video) &&
          !video.muted &&
          video.volume > 0
        ) {
          muteOtherVideos(video);
        }
      });

      video.addEventListener('loadedmetadata', () => scheduleRefresh(20));

      video.addEventListener('canplay', () => {
        if (slideIsActive(getSlide(video))) scheduleRefresh(10);
      });

      video.addEventListener('ended', () => {
        if (!video.loop) userPaused.add(video);
      });
    });
  };

  const registerGallery = (gallery) => {
    if (galleries.has(gallery)) return;
    galleries.add(gallery);

    bindGalleryControls(gallery);

    const mutationObserver = new MutationObserver(() => {
      scheduleRefresh(30);
    });

    mutationObserver.observe(gallery, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class', 'aria-hidden', 'style']
    });

    let intersectionObserver = null;

    if ('IntersectionObserver' in window) {
      intersectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            visibility.set(gallery, entry.isIntersecting);

            if (!entry.isIntersecting) {
              gallery.querySelectorAll(VIDEO_SELECTOR).forEach((video) => {
                pauseVideo(video, true);
              });
            }
          });

          scheduleRefresh(10);
        },
        { threshold: 0.12 }
      );

      intersectionObserver.observe(gallery);
    } else {
      visibility.set(gallery, true);
    }

    observers.set(gallery, {
      mutationObserver,
      intersectionObserver
    });
  };

  const cleanupDisconnectedGalleries = () => {
    galleries.forEach((gallery) => {
      if (document.documentElement.contains(gallery)) return;

      const observerSet = observers.get(gallery);
      observerSet?.mutationObserver?.disconnect();
      observerSet?.intersectionObserver?.disconnect();

      observers.delete(gallery);
      galleries.delete(gallery);
    });
  };

  const documentObserver = new MutationObserver(() => {
    cleanupDisconnectedGalleries();
    scheduleRefresh(40);
  });

  const init = () => {
    document.querySelectorAll(GALLERY_SELECTOR).forEach(registerGallery);

    documentObserver.observe(document.body, {
      childList: true,
      subtree: true
    });

    window.addEventListener(
      'resize',
      () => scheduleRefresh(100),
      { passive: true }
    );

    window.addEventListener(
      'load',
      () => scheduleRefresh(30),
      { once: true }
    );

    document.addEventListener(
      'shopify:section:load',
      () => scheduleRefresh(30)
    );

    document.addEventListener(
      'shopify:section:unload',
      cleanupDisconnectedGalleries
    );

    if (document.fonts?.ready) {
      document.fonts.ready.then(() => scheduleRefresh(20));
    }

    scheduleRefresh();
  };

  window[GLOBAL_KEY] = {
    initialized: true,
    refresh: () => scheduleRefresh(),
    destroy: () => {
      documentObserver.disconnect();

      observers.forEach(({ mutationObserver, intersectionObserver }) => {
        mutationObserver?.disconnect();
        intersectionObserver?.disconnect();
      });

      galleries.clear();
      observers.clear();

      window.clearTimeout(refreshTimer);
      if (refreshFrame) window.cancelAnimationFrame(refreshFrame);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
